import { Controller } from '@hotwired/stimulus'

// Drives the chapter narration player: play/pause, a seekable progress track,
// elapsed/total time, and speed cycling. Karaoke word-highlighting is layered on
// in a later step (it reads the timeline target). Auto-registered by the Vite
// controllers glob.
const SPEEDS = [1, 1.25, 1.5, 0.75]

export default class extends Controller {
  static targets = ['audio', 'toggle', 'playIcon', 'pauseIcon', 'track', 'fill', 'current', 'duration', 'speed', 'readAlong', 'timeline', 'prose']
  static values = { ready: Boolean, playLabel: String, pauseLabel: String }

  connect() {
    if (!this.hasAudioTarget) return
    this._onTime = this.onTimeUpdate.bind(this)
    this._onMeta = this.onLoadedMetadata.bind(this)
    this._onEnded = this.onEnded.bind(this)
    this.audioTarget.addEventListener('timeupdate', this._onTime)
    this.audioTarget.addEventListener('loadedmetadata', this._onMeta)
    this.audioTarget.addEventListener('ended', this._onEnded)
    this._speedIndex = 0
    this.setupKaraoke()
  }

  disconnect() {
    if (!this.hasAudioTarget) return
    this.audioTarget.removeEventListener('timeupdate', this._onTime)
    this.audioTarget.removeEventListener('loadedmetadata', this._onMeta)
    this.audioTarget.removeEventListener('ended', this._onEnded)
  }

  toggle() {
    if (this.audioTarget.paused) this.audioTarget.play()
    else this.audioTarget.pause()
    this.reflectPlayState()
  }

  reflectPlayState() {
    const playing = !this.audioTarget.paused
    this.playIconTarget.classList.toggle('hidden', playing)
    this.pauseIconTarget.classList.toggle('hidden', !playing)
    // a11y: the toggle button's label must reflect the action it performs now.
    const label = playing ? this.pauseLabelValue : this.playLabelValue
    if (label) this.toggleTarget.setAttribute('aria-label', label)
  }

  onLoadedMetadata() {
    this.durationTarget.textContent = this.format(this.audioTarget.duration)
  }

  onTimeUpdate() {
    const { currentTime, duration } = this.audioTarget
    if (duration > 0) this.fillTarget.style.width = `${(currentTime / duration) * 100}%`
    this.currentTarget.textContent = this.format(currentTime)
    this.highlightAt(currentTime * 1000)
  }

  onEnded() {
    this.reflectPlayState()
  }

  seek(event) {
    const rect = this.trackTarget.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
    if (this.audioTarget.duration > 0) this.audioTarget.currentTime = ratio * this.audioTarget.duration
  }

  cycleSpeed() {
    this._speedIndex = (this._speedIndex + 1) % SPEEDS.length
    const rate = SPEEDS[this._speedIndex]
    this.audioTarget.playbackRate = rate
    this.speedTarget.textContent = `${rate.toFixed(2).replace(/0$/, '')}×`
  }

  toggleReadAlong() {
    this._readAlong = !(this._readAlong ?? true)
    this.readAlongTarget.setAttribute('aria-pressed', String(this._readAlong))
    this.readAlongTarget.classList.toggle('bg-accent/10', this._readAlong)
    this.readAlongTarget.classList.toggle('text-accent', this._readAlong)
    if (!this._readAlong) this.words?.forEach((w) => w.classList.remove('is-current'))
  }

  setupKaraoke() {
    this.words = []
    if (!this.hasTimelineTarget || !this.hasProseTarget) return
    // Guard against double-wrapping: a Turbo cache restore can re-insert prose
    // that already holds .narr-word spans, and a second wrap would nest them.
    if (this.proseTarget.querySelector('.narr-word')) return
    let rawTimeline
    try {
      rawTimeline = JSON.parse(this.timelineTarget.textContent || '[]')
    } catch { rawTimeline = [] }
    // The narration is synthesized from an EXPRESSIVE SCRIPT: the prose with
    // fish.audio emotion markers in square brackets (e.g. [excited], [whispering])
    // inserted. fish.audio's word timeline may include those [marker] tokens as
    // "words", but the body text shown to the reader is the ORIGINAL prose with NO
    // markers. Drop any timeline entry that is entirely a bracketed marker so the
    // remaining spoken words line up with the body words index-for-index.
    this.timeline = rawTimeline.filter((seg) => !/^\[[^\]]*\]$/.test((seg.w || '').trim()))
    if (this.timeline.length === 0) return

    const spans = this.wrapWords(this.proseTarget)
    // Fallback: if the visible word count and the (filtered) timeline diverge
    // beyond a small tolerance, highlighting would mis-align. Unwrap and play
    // audio only.
    const diff = Math.abs(spans.length - this.timeline.length)
    if (diff > Math.max(3, this.timeline.length * 0.1)) {
      this.unwrap(spans)
      return
    }
    this.words = spans
    this._current = -1
  }

  // Wrap each whitespace-delimited word in the prose container's text nodes in a
  // span, preserving order. Skips script/style. Returns the created spans.
  wrapWords(root) {
    const spans = []
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => n.nodeValue.trim() && n.parentElement.tagName !== 'SCRIPT'
        ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
    })
    const textNodes = []
    while (walker.nextNode()) textNodes.push(walker.currentNode)
    textNodes.forEach((node) => {
      const frag = document.createDocumentFragment()
      node.nodeValue.split(/(\s+)/).forEach((tok) => {
        if (/^\s+$/.test(tok) || tok === '') { frag.appendChild(document.createTextNode(tok)); return }
        const span = document.createElement('span')
        span.className = 'narr-word'
        span.textContent = tok
        spans.push(span)
        frag.appendChild(span)
      })
      node.parentNode.replaceChild(frag, node)
    })
    return spans
  }

  unwrap(spans) {
    spans.forEach((s) => { if (s.parentNode) s.parentNode.replaceChild(document.createTextNode(s.textContent), s) })
  }

  highlightAt(ms) {
    if (!this.words.length || this._readAlong === false) return
    // binary search the timeline for the segment containing ms
    let lo = 0, hi = this.timeline.length - 1, idx = -1
    while (lo <= hi) {
      const mid = (lo + hi) >> 1
      const seg = this.timeline[mid]
      if (ms < seg.s) hi = mid - 1
      else if (ms > seg.e) lo = mid + 1
      else { idx = mid; break }
    }
    if (idx === this._current) return
    if (idx >= 0 && idx < this.words.length) {
      this.words.forEach((w, i) => {
        w.classList.toggle('is-current', i === idx)
        w.classList.toggle('is-done', i < idx)
      })
      this.scrollIntoView(this.words[idx])
      this._current = idx
    }
  }

  scrollIntoView(el) {
    const rect = el.getBoundingClientRect()
    if (rect.top < 80 || rect.bottom > window.innerHeight - 120) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  format(seconds) {
    if (!isFinite(seconds)) return '0:00'
    const s = Math.floor(seconds % 60).toString().padStart(2, '0')
    return `${Math.floor(seconds / 60)}:${s}`
  }
}
